/**
 * The seller dashboard's pages and their paths — and nothing else.
 *
 * Split from `nav.ts` for the reason `admin-paths.ts` is: the router imports this, so
 * whatever it holds ships in the main bundle every buyer downloads. The nav's labels and
 * icons stay in `nav.ts`, which only the lazy `SellerDashArea` chunk imports.
 *
 * **Why `/seller/dashboard/*` and not `/admin/…`** (D46 addendum, 2026-10-12). The gate is
 * what keeps this off sellers' screens, not the path — `adminAccess()` in `lib/access.ts`,
 * the same one the admin console uses. The URL string carries no policy. And when
 * impersonation lands (**O42**, open), `/admin/seller-dashboard/{sellerId}/orders` would
 * need a parameter *and* nesting, two of the three triggers `routes.ts` writes down for
 * abandoning the hand-rolled router; `/seller/dashboard/orders` takes the seller from the
 * session and never needs one. Nothing here builds impersonation, a session swap or any
 * elevation — picking the prefix does not settle O42, it only avoids a rename.
 *
 * The page ids are the design's own (`NAV.seller` in `Seller Dashboard Shell.dc.html`),
 * so a label and its page can be compared against the design without a translation table.
 */

export type SellerPage =
  // Selling
  | 'home'
  | 'messages'
  | 'inv-products'
  | 'inv-ai'
  | 'inv-import'
  | 'shows-upcoming'
  | 'shows-schedule'
  | 'shows-past'
  | 'clips'
  // Orders
  | 'orders'
  | 'ship-settings'
  | 'ship-profiles'
  | 'ship-carrier'
  // Growth
  | 'aud-buyers'
  | 'aud-followers'
  | 'aud-blocks'
  | 'mkt-refer'
  | 'mkt-push'
  // Business
  | 'health'
  | 'fin-ledger'
  | 'fin-payouts'
  | 'fin-reporting'
  | 'fin-tax'
  | 'analytics'
  // Setup
  | 'integrations'
  | 'set-team'
  | 'set-notifications'
  | 'audit';

/**
 * Static paths only (D46 addendum, 2026-10-08): twenty-eight literal entries (the design's twenty-seven and Settings → Notifications, D71), no
 * parameter, so this stays inside the hand-rolled router's trigger. `/seller/dashboard`
 * itself is the default page.
 */
export const SELLER_PATHS: Readonly<Record<SellerPage, string>> = {
  home: '/seller/dashboard',
  messages: '/seller/dashboard/messages',
  'inv-products': '/seller/dashboard/products',
  'inv-ai': '/seller/dashboard/products/create-with-ai',
  'inv-import': '/seller/dashboard/products/bulk-import',
  'shows-upcoming': '/seller/dashboard/shows/upcoming',
  'shows-schedule': '/seller/dashboard/shows/schedule',
  'shows-past': '/seller/dashboard/shows/past',
  clips: '/seller/dashboard/clips',
  orders: '/seller/dashboard/orders',
  'ship-settings': '/seller/dashboard/shipments/settings',
  'ship-profiles': '/seller/dashboard/shipments/profiles',
  'ship-carrier': '/seller/dashboard/shipments/carrier-adjustments',
  'aud-buyers': '/seller/dashboard/audience/buyers',
  'aud-followers': '/seller/dashboard/audience/followers',
  'aud-blocks': '/seller/dashboard/audience/block-list',
  'mkt-refer': '/seller/dashboard/marketing/refer-and-earn',
  'mkt-push': '/seller/dashboard/marketing/push-notifications',
  health: '/seller/dashboard/account-health',
  'fin-ledger': '/seller/dashboard/finance/ledger',
  'fin-payouts': '/seller/dashboard/finance/payouts',
  'fin-reporting': '/seller/dashboard/finance/reporting',
  'fin-tax': '/seller/dashboard/finance/tax-documents',
  analytics: '/seller/dashboard/analytics',
  integrations: '/seller/dashboard/integrations',
  'set-team': '/seller/dashboard/settings/team',
  'set-notifications': '/seller/dashboard/settings/notifications',
  audit: '/seller/dashboard/audit',
};

/** The design opens on the store's own page — its `home`. */
export const SELLER_DEFAULT_PAGE: SellerPage = 'home';
